import { EntityKindHandler, type EntityMenuContext, type AddPickerEntry, type EntityInteractionContext, type EntityClickResult } from '../EntityKindHandler';
import type { CanvasEntity, ConnectorPosition } from '../../entityTypes';
import { getAdrTriggerData } from '../../entityTypes';
import { AdrTriggerNode } from './AdrTriggerNode';
import type { ContextMenuItem } from '../../../components/ContextMenu';
import { Trash2 } from 'lucide-react';
import { canvasSelectionStore } from '../../stores/canvasSelectionStore';
import { buildColorGridSubmenu } from '../../lib/contextMenuHelpers';
import i18n from '../../../i18n';

const t = (key: string) => i18n.t(key as any) as string;

const ADR_TRIGGER_COLORS = [
  { id: 'red', label: 'Rot', color: '#ef4444' },
  { id: 'orange', label: 'Orange', color: '#f97316' },
  { id: 'amber', label: 'Amber', color: '#f59e0b' },
  { id: 'green', label: 'Grün', color: '#22c55e' },
  { id: 'blue', label: 'Blau', color: '#3b82f6' },
  { id: 'purple', label: 'Lila', color: '#a855f7' },
  { id: 'pink', label: 'Pink', color: '#ec4899' },
  { id: 'gray', label: 'Grau', color: '#6b7280' },
];

export class AdrTriggerHandler extends EntityKindHandler {
  readonly kind = 'adr-trigger';
  readonly Component = AdrTriggerNode;
  readonly dataAttribute = 'data-canvas-adr-trigger';
  canDrag = true;
  canSnap = true;
  canBoxSelect = true;
  canConnect = false;
  canResize = false;
  canInlineEdit = false;

  getAddPickerEntry(): AddPickerEntry | null {
    // ADR triggers should only be created by dragging from the ADR sidebar list,
    // which provides the actual ADR ID. Adding via context menu would create
    // a trigger with no linked ADR.
    return null;
  }

  getConnectorPositions(_entity: CanvasEntity): ConnectorPosition[] {
    return [];
  }

  getContextMenuItems(ctx: EntityMenuContext): ContextMenuItem[] {
    const { store, entity } = ctx;
    const d = getAdrTriggerData(entity);
    const items: ContextMenuItem[] = [];

    // Color picker
    items.push(buildColorGridSubmenu({
      id: 'trigger-color',
      label: t('adr.trigger.color'),
      currentColor: d.color || '#ef4444',
      handlerColors: ADR_TRIGGER_COLORS,
      onSelectColor: (color) => store.send({
        type: 'adr-trigger.updateData',
        entityId: entity.id,
        dataChanges: { color },
      }),
    }));

    // Delete trigger
    items.push({
      id: 'delete-trigger',
      label: t('adr.trigger.deleteTrigger'),
      icon: Trash2,
      iconColor: '#dc2626',
      separator: true,
      onClick: () => {
        store.send({ type: 'entity.remove', entityId: entity.id });
        canvasSelectionStore.send({ type: 'entity.clear' });
      },
    });

    return items;
  }

  onEntityClick(ctx: EntityInteractionContext, _shiftKey: boolean): EntityClickResult | null {
    const data = getAdrTriggerData(ctx.entity);
    if (data.adrIds.length === 1) {
      return {
        afterSelect: () => {
          ctx.uiStore.send({ type: 'adrDetail.open', adrId: data.adrIds[0] });
        },
      };
    }
    // Multi-ADR case is handled by local component state (setMenuOpen toggle)
    return null;
  }
}
